import * as React from 'react';
import {FunctionComponent} from 'react';
import {OperatingSystemLogo} from '../OperatingSystemLogo';
import {BrowserLogo} from '../BrowserLogo';
import {ShrinkExpandFlatDetails} from '../ShrinkExpandFlatDetails';
import {FlatDocument, WebDeviceInfo} from '../../swagger';
import {FlatListForLogGenericState} from './FlatListForLogGenericState';
import {observer} from 'mobx-react-lite';
import {Button} from '../Button';

export interface FlatDetailsDevicesProps {
    f: FlatDocument;
    state: FlatListForLogGenericState;
}

interface BrowserStat {
    name: string;
    count: number;
}

interface OSStat {
    name: string;
    osFamily: string;
    count: number;
}

function groupbyBrowser(deviceInfo: { [key: string]: WebDeviceInfo; } | null): BrowserStat[] {
    if (!deviceInfo) return [];

    const browsers: { [key: string]: number } = {};
    Object.values(deviceInfo).forEach(d => {
        const k = d.family!;
        browsers[k] = (browsers[k] ?? 0) + (d?.count ?? 0);
    });
    let browsersData: BrowserStat[] = [];

    Object.keys(browsers).forEach(k => {
        browsersData.push({
            name: k,
            count: browsers[k],
        });
    });

    browsersData = browsersData.sort((a, b) => b.count - a.count);

    return browsersData;

}

function groupbyOS(deviceInfo: { [key: string]: WebDeviceInfo; } | null): OSStat[] {
    if (!deviceInfo) return [];

    const systems: { [key: string]: number } = {};
    Object.values(deviceInfo).forEach(d => {
        const k = d.osFamily! + ' ' + (d.osMajor ?? '');
        systems[k] = (systems[k] ?? 0) + (d?.count ?? 0);
    });
    let systemsData: OSStat[] = [];
    Object.keys(systems).forEach(k => {
        systemsData.push({
            name: k,
            osFamily: k.split(' ')[0],
            count: systems[k],
        });
    });
    systemsData = systemsData.sort((a, b) => b.count - a.count);


    return systemsData;
}

export const FlatDetailsDevices: FunctionComponent<FlatDetailsDevicesProps> = observer(({f, state}) =>
    <ShrinkExpandFlatDetails
        id={f.flatId}
        title={'Browsers and devices'}
        defaultExpanded={false}
        onExpand={() => state.loadFlatDeviceInfo()}
        loading={state.flatDeviceInfoLoading}>
        {(f.deviceInfo !== undefined && Object.values(f.deviceInfo!).length === 0) && <div>No data</div>}
        {f.deviceInfo && <>
            {/*  <FlatDevicesGraph data={f.deviceInfo}/>*/}
            <div className="grid grid-cols-2 gap-4 mb-2">
                <div className="">
                    <h3 className="mb-2">Browser occurencies count</h3>
                    {groupbyBrowser(f.deviceInfo).map(b => {
                        return <div key={'bstat' + b.name} className="mb-2"><BrowserLogo name={b.name} className="inline relative w-8 mr-2"/>
                            {b.name}<span
                                className="ml-4 inline-flex items-center justify-center px-2 py-1 mr-2 text-xs font-bold leading-none text-m-gray-2 bg-m-green rounded-full">{b.count}</span><br/>
                        </div>;
                    })}
                </div>
                <div>
                    <h3 className="mb-2">Operating system occurencies count</h3>
                    {groupbyOS(f.deviceInfo).map(b => {
                        return <div key={'bstat' + b.name} className="mb-2"><OperatingSystemLogo name={b.osFamily} className="inline relative w-8 mr-2"/>
                            {b.name}<span
                                className="ml-4 inline-flex items-center justify-center px-2 py-1 mr-2 text-xs font-bold leading-none text-m-gray-2 bg-m-green rounded-full">{b.count}</span><br/>
                        </div>;
                    })}
                </div>
            </div>

            <Button className="btn-primary" onClick={() => state.toggleDeviceInfoDetails()}>{state.deviceInfoDetails ? 'Hide details' : 'Show details'}</Button>
            {state.deviceInfoDetails && <table>
                <thead className="text-left">
                <tr>
                    <th>OS</th>
                    <th>Device</th>
                    <th>Browser</th>
                    <th>Count</th>
                </tr>
                </thead>
                <tbody>
                {f.deviceInfo && Object.values(f.deviceInfo).sort((a, b) => b.count! - a.count!).map((d) => {
                    return (
                        <tr key={'di' + d.key} className="border-b-1 border-m-gray-1 m-2 hover:bg-m-gray-2 p-2">
                            <td><OperatingSystemLogo className="w-4 inline relative" name={d.osFamily}/> {d.osMajor}&nbsp; </td>
                            <td>{d.deviceBrand} {d.deviceModel}&nbsp;</td>
                            <td><BrowserLogo className="inline relative w-4" name={d.family}/> {d.major}.{d.minor}&nbsp;</td>
                            <td><span title="Count" className="badge mr-2 right-0">{d.count}</span></td>

                        </tr>
                    );
                })}
                </tbody>
            </table>}
        </>}
    </ShrinkExpandFlatDetails>);
